'use client';

import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';

import { cn } from '@/lib/cn';
import { useWorkspaceScope } from '../../../hooks/useWorkspaceScope';
import {
  useMarkAllUnifiedNotificationsAsReadMutation,
  useMarkUnifiedNotificationAsReadMutation,
  useUnifiedNotifications,
  useUnifiedUnreadCount,
} from '../../../hooks/queries/useNotifications';
import type { NotificationItem } from '../../../lib/api/notifications';
import { buildHomeNotificationHref } from '../../../lib/notifications/homeLink';
import { resolveAppHref } from '../../../lib/portalRoutes';
import { NotificationsIcon } from '../Sidebar/icons';

type NotificationBellProps = {
  token: string | null;
};

const formatTimeAgo = (dateStr: string, t: (key: string, values?: Record<string, number>) => string): string => {
  const now = Date.now();
  const date = new Date(dateStr).getTime();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60_000);
  const diffHour = Math.floor(diffMs / 3_600_000);
  const diffDay = Math.floor(diffMs / 86_400_000);

  if (diffMin < 1) return t('justNow');
  if (diffMin < 60) return t('minutesAgo', { count: diffMin });
  if (diffHour < 24) return t('hoursAgo', { count: diffHour });
  return t('daysAgo', { count: diffDay });
};

function NotificationItemRow({
  notification,
  onRead,
  tPage,
  tType,
}: {
  notification: NotificationItem;
  onRead: (n: NotificationItem) => void;
  tPage: (key: string, values?: Record<string, number>) => string;
  tType: (key: string) => string;
}) {
  const isUnread = notification.status === 'UNREAD';

  return (
    <button
      type="button"
      className={cn(
        'flex w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-base-200/60',
        isUnread && 'bg-primary/5',
      )}
      onClick={() => onRead(notification)}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] text-[var(--color-fg-subtle)]">{tType(notification.type)}</span>
          {notification.project?.name ? (
            <span className="rounded bg-base-200 px-1 py-0.5 text-[10px] text-[var(--color-fg-subtle)]">
              {notification.project.name}
            </span>
          ) : null}
          {isUnread ? <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" /> : null}
        </div>
        <p className="mt-0.5 truncate text-sm font-medium text-base-content">{notification.title}</p>
        <p className="mt-0.5 truncate text-xs text-[var(--color-fg-muted)]">{notification.body}</p>
        <span className="mt-1 text-[11px] text-[var(--color-fg-subtle)]">
          {formatTimeAgo(notification.createdAt, tPage)}
        </span>
      </div>
    </button>
  );
}

export const NotificationBell = ({ token }: NotificationBellProps) => {
  const tBell = useTranslations('notifications.bell');
  const tPage = useTranslations('notifications.page');
  const tType = useTranslations('notifications.type');
  const router = useRouter();
  const { contextQuery } = useWorkspaceScope();

  const { refs, floatingStyles } = useFloating({
    placement: 'bottom-end',
    middleware: [offset(6), flip({ padding: 12 }), shift({ padding: 12 })],
    whileElementsMounted: autoUpdate,
  });

  const { data: notificationsData } = useUnifiedNotifications(token, { page: 1, limit: 5 });
  const { data: unreadCount = 0 } = useUnifiedUnreadCount(token);
  const markAsReadMutation = useMarkUnifiedNotificationAsReadMutation(token);
  const markAllAsReadMutation = useMarkAllUnifiedNotificationsAsReadMutation(token);

  const notifications = notificationsData?.data ?? [];

  const handleNotificationClick = (notification: NotificationItem) => {
    if (notification.status === 'UNREAD') {
      markAsReadMutation.mutate({
        id: notification.id,
        entityId: notification.entityId,
        type: notification.type,
      });
    }

    router.push(resolveAppHref(buildHomeNotificationHref(notification)));
  };

  const handleMarkAllAsRead = () => {
    markAllAsReadMutation.mutate();
  };

  const displayCount = unreadCount > 99 ? '99+' : unreadCount;

  return (
    <Popover className="relative">
      <PopoverButton
        ref={refs.setReference}
        aria-label={tBell('tooltip')}
        title={tBell('tooltip')}
        className="btn btn-square btn-ghost btn-sm relative shrink-0 focus:outline-none"
      >
        <NotificationsIcon className="h-5 w-5" />
        {unreadCount > 0 ? (
          <span className="absolute -right-0.5 -top-0.5 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-error px-1 text-[10px] font-bold leading-none text-[var(--color-fg-on-primary)]">
            {displayCount}
          </span>
        ) : null}
      </PopoverButton>

      <PopoverPanel
        ref={refs.setFloating}
        style={floatingStyles}
        className="z-[50] w-80 rounded-xl border border-base-300 bg-base-100 shadow-xl"
      >
        {({ close }) => (
          <>
            <div className="flex items-center justify-between border-b border-base-200 px-4 py-3">
              <span className="text-sm font-semibold text-base-content">{tBell('tooltip')}</span>
              {unreadCount > 0 ? (
                <button
                  type="button"
                  className="text-xs text-primary hover:text-primary/80"
                  onClick={handleMarkAllAsRead}
                >
                  {tBell('markAllAsRead')}
                </button>
              ) : null}
            </div>

            <div className="max-h-80 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-[var(--color-fg-muted)]">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="mb-2 h-8 w-8"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.5"
                      d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                    />
                  </svg>
                  <p className="text-sm">{tBell('empty')}</p>
                  <p className="mt-0.5 text-xs">{tBell('emptyDescription')}</p>
                </div>
              ) : (
                <div className="p-1">
                  {notifications.map((notification) => (
                    <div key={notification.id} onClick={() => close()}>
                      <NotificationItemRow
                        notification={notification}
                        onRead={handleNotificationClick}
                        tPage={tPage}
                        tType={tType}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="border-t border-base-200 px-4 py-2.5">
              <Link
                href={resolveAppHref(`/notifications${contextQuery}`)}
                className="block text-center text-xs text-primary hover:text-primary/80"
                onClick={() => close()}
              >
                {tBell('viewAll')}
              </Link>
            </div>
          </>
        )}
      </PopoverPanel>
    </Popover>
  );
};
